import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ChatAIService, ChatMessage } from '../../services/chat-ai.service';
import { TutorContextStateService, TutorContext } from '../../services/tutor-context-state.service';
import { TutorMessage } from '../../services/tutor-ai.service';
import { AuthService } from '../../services/auth.service';
import { StorageService } from '../../services';

@Component({
    selector: 'app-floating-chat',
    templateUrl: './floating-chat.component.html',
    styleUrls: ['./floating-chat.component.scss']
})
export class FloatingChatComponent implements OnInit, OnDestroy {
    // Estados
    isOpen = false;
    isLearning = false;
    isSupport = false;
    isTutor = false;
    isCourseDetailVisible = false;

    // Control de rol
    canShowSupport = true;

    // Mensajes y datos
    chatMessages: ChatMessage[] = [];
    tutorMessages: TutorMessage[] = [];
    tutorContext: TutorContext | null = null;
    showClearModal = false;

    private destroy$ = new Subject<void>();
    private initialized = false;
    private playerContextListener: (event: MessageEvent) => void;

    constructor(
        private chatAIService: ChatAIService,
        private tutorContextStateService: TutorContextStateService,
        private authService: AuthService,
        private storageService: StorageService,
        private router: Router
    ) { }

    /**
     * Getter que verifica si el usuario está autenticado en tiempo real
     */
    get isAuthenticated(): boolean {
        return this.authService.isLogged();
    }

    /**
     * Getter que verifica si el módulo IA está activado en la empresa
     * El chat flotante completo solo se muestra si el módulo IA está activo
     */
    get canShowChat(): boolean {
        const company = this.storageService.get('company');
        return company?.permissions?.companyAiAssistant === true;
    }

    /**
     * Getter que verifica dinámicamente si el módulo IA está activado y el usuario está en la vista /learn
     * Se evalúa cada vez que se accede, reflejando cambios en el storage y la ruta
     */
    get canShowLearning(): boolean {
        const company = this.storageService.get('company');

        // El módulo IA debe estar activo
        const aiEnabled = company?.permissions?.companyAiAssistant === true;

        // El usuario debe estar en la vista /learn (no en /organize, /manage, etc)
        const isInLearnView = this.router.url.includes('/learn');

        return aiEnabled && isInLearnView;
    }

    get canShowTutor(): boolean {
        const company = this.storageService.get('company');
        const aiEnabled = company?.permissions?.companyAiAssistant === true;
        return aiEnabled && !!this.tutorContext;
    }

    ngOnInit(): void {
        this.initializeIfAuthenticated();

        // Escuchar cambios de navegación para detectar cuando el usuario inicia sesión
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.initializeIfAuthenticated();
        });

        // Suscribirse al contexto del tutor (detail view + player via postMessage)
        this.tutorContextStateService.currentContext$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(ctx => {
            this.tutorContext = ctx;
            if (!ctx && this.isTutor) {
                this.isTutor = false;
            }
        });

        // Escuchar postMessages del player (contexto de sección y actividad activa)
        this.playerContextListener = (event: MessageEvent) => {
            if (event.data?.type === 'tutor_context') {
                this.tutorContextStateService.setContext({
                    source: 'player',
                    contentType: 'player-' + (event.data.contentType || 'content'),
                    contentId: event.data.courseId,
                    contentTitle: event.data.courseTitle,
                    contentDescription: event.data.courseDescription || event.data.courseTitle || '',
                    sectionId: event.data.sectionId,
                    sectionTitle: event.data.sectionTitle,
                    sectionDescription: event.data.sectionDescription || '',
                    activityId: event.data.activityId,
                    activityTitle: event.data.activityTitle,
                    activityDescription: event.data.activityDescription || '',
                });
            }
        };
        window.addEventListener('message', this.playerContextListener);
    }

    /**
     * Inicializa el historial del chat solo cuando el usuario está autenticado
     */
    private initializeIfAuthenticated(): void {
        if (this.isAuthenticated && !this.initialized) {
            this.initialized = true;
            this.loadChatHistory();
        }
    }

    /**
     * Carga el historial del chat desde localStorage
     */
    private loadChatHistory(): void {
        const history = this.chatAIService.getChatHistory();
        this.chatMessages = history;
    }

    /**
     * Abre/cierra el chat
     */
    toggleChat(): void {
        this.isOpen = !this.isOpen;
    }

    /**
     * Cierra el chat
     */
    closeChat(): void {
        this.isOpen = false;
    }

    /**
     * Activa la pestaña de aprendizaje
     */
    activateLearning(): void {
        // No cargar historial aquí para evitar duplicados - ya se cargó en ngOnInit
        this.isLearning = true;
        this.isSupport = false;
        this.isTutor = false;
        this.isCourseDetailVisible = false;
    }

    /**
     * Activa la pestaña de soporte
     */
    activateSupport(): void {
        // No cargar historial aquí para evitar duplicados - ya se cargó en ngOnInit
        this.isLearning = false;
        this.isSupport = true;
        this.isTutor = false;
        this.isCourseDetailVisible = false;
    }

    activateTutor(): void {
        this.isTutor = true;
        this.isLearning = false;
        this.isSupport = false;
        this.isCourseDetailVisible = false;
    }

    onTutorMessage(message: TutorMessage): void {
        this.tutorMessages.push(message);
    }

    /**
     * Maneja el mensaje enviado desde el componente hijo de aprendizaje
     */
    onLearningMessage(message: ChatMessage): void {
        this.chatMessages.push(message);
        this.chatAIService.addChatMessage(message);
    }

    /**
     * Maneja el mensaje de soporte
     */
    onSupportMessage(message: ChatMessage): void {
        this.chatMessages.push(message);
        this.chatAIService.addChatMessage(message);
    }

    /**
     * Maneja cuando se selecciona o deselecciona un curso
     */
    onCourseSelected(isSelected: boolean): void {
        this.isCourseDetailVisible = isSelected;
    }

    /**
     * Muestra el modal de confirmación para limpiar el historial
     */
    clearChat(): void {
        this.showClearModal = true;
    }

    /**
     * Confirma y limpia el historial del chat
     */
    confirmClearChat(): void {
        this.chatMessages = [];
        this.tutorMessages = [];
        this.chatAIService.clearChatHistory();
        this.showClearModal = false;
    }

    /**
     * Cancela la limpieza del historial
     */
    cancelClearChat(): void {
        this.showClearModal = false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.playerContextListener) {
            window.removeEventListener('message', this.playerContextListener);
        }
    }
}
